import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import ar from './locales/ar.json'

const LANG_KEY = 'meno-lang'
const LANG_COOKIE = 'wetook_lang'

export const LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'عربي',   dir: 'rtl' },
] as const

export type LangCode = (typeof LANGUAGES)[number]['code']

const isLang = (v: unknown): v is LangCode => LANGUAGES.some((l) => l.code === v)

// ── Cross-app persistence ──────────────────────────────────────────────────
// localStorage is per-origin, so a choice on frame.wetook.net wouldn't carry to
// my/ping/auth.wetook.net. We ALSO store the language in a cookie scoped to the
// registrable domain so every subdomain sees it. Scope: `.wetook.net` in
// prod/staging (covers *.wetook.net incl. *.staging.wetook.net); host-only on
// localhost (dev apps share it across ports since cookies aren't port-scoped).
function cookieDomainAttr(): string {
  const h = typeof location !== 'undefined' ? location.hostname : ''
  return h.endsWith('wetook.net') ? '; domain=.wetook.net' : ''
}
function readLangCookie(): LangCode | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)wetook_lang=([^;]+)/)
  return m && isLang(m[1]) ? (m[1] as LangCode) : null
}
function writeLangCookie(code: LangCode) {
  if (typeof document === 'undefined') return
  // 1-year, lax so it rides top-level cross-subdomain navigations.
  document.cookie = `${LANG_COOKIE}=${code}; path=/${cookieDomainAttr()}; max-age=31536000; samesite=lax`
}

/** Best language when the user has never chosen one: the browser/device
 *  preference (navigator.languages), matched against the supported set; else en. */
function browserDefault(): LangCode {
  const prefs = (typeof navigator !== 'undefined'
    ? navigator.languages ?? [navigator.language]
    : []) as string[]
  for (const p of prefs) {
    const base = p?.toLowerCase().split('-')[0]
    if (isLang(base)) return base as LangCode
  }
  return 'en'
}

/** Apply dir + lang attributes to <html>, and persist the choice both
 *  per-origin (localStorage) and cross-subdomain (cookie). */
export function applyLanguage(code: LangCode) {
  const lang = LANGUAGES.find((l) => l.code === code)!
  document.documentElement.lang = code
  document.documentElement.dir  = lang.dir
  localStorage.setItem(LANG_KEY, code)
  writeLangCookie(code)
}

// Precedence: an explicit cross-app choice (cookie) → this app's last choice
// (localStorage) → the browser/device default. No hard-coded 'ar' default.
const stored = localStorage.getItem(LANG_KEY)
const saved: LangCode =
  readLangCookie() ?? (isLang(stored) ? (stored as LangCode) : null) ?? browserDefault()

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    lng: saved,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  })

// Apply on load
applyLanguage(saved)

export default i18n
